import React, { memo, useEffect, useState } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { each, find, flatten, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { severityOptionsWithAll } from 'components/DataDisplay/Utils/renderSeverity';
import { ButtonGroup, Select } from 'components/DataEntry';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import {
  ChartMixedIcon,
  EnvironmentIcon,
  IconTypes,
  PolicyIcon,
  ThirdPartyIcon,
  ViewAllIcon,
} from 'components/Icons';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { selectApp } from 'containers/App/selectors';
import { ComplianceAssessment } from 'containers/Compliance/Components/ComplianceAssessment';
import { ComplianceDashboard } from 'containers/Compliance/Components/ComplianceDashboard';
import { ComplianceTableTabs } from 'containers/Compliance/Components/ComplianceTables/ComplianceTableTabs';
import { buttonGroupItems } from 'containers/Compliance/Components/utils';
import { selectCompliance } from 'containers/Compliance/selectors';
import { useBlueprintsMapping } from 'containers/SharedState/hooks/useBlueprintsMapping';
import { useSearchParams } from 'hooks/useSearchParams';

import { actions } from '../slice';

export const ComplianceSummary = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useSearchParams<{ blueprint_id?: string }>();
  const { environmentMapping } = useEnvironmentOptions();
  const {
    selectedBlueprint,
    resourceTypeSeverity: severity,
    resourceType,
  } = useSelector(selectCompliance);
  const { cloudAccounts } = useSelector(selectApp);
  const [blueprintOptions, setBlueprintOptions] = useState<
    Record<string, any>[]
  >([]);
  const [environmentOptions, setEnvironmentOptions] = useState<
    Record<string, any>[]
  >([]);
  const [blueprint, setBlueprint] = useState<Record<string, any>>({});
  const [environment, setEnvironment] = useState<Record<string, any>>({});
  const [resourceTypeOptions, setResourceTypeOptions] = useState<
    Record<string, any>[]
  >([]);
  const [tab, setTab] = useState(buttonGroupItems[0]);
  const [showDashboard, setShowDashboard] = useState(false);
  const { getCategorizedBlueprintOptions } = useBlueprintsMapping();
  const [isExpanded, setIsExpanded] = useState(false);
  const { resourceTypeAliasOptionsWithAll } = useResourceType();

  /*set blueprint options on mount*/
  useEffect(() => {
    const options = getCategorizedBlueprintOptions(true);
    setBlueprintOptions(options);
    const option = options?.[0]?.options?.[0];
    setBlueprint(options?.[0]?.options?.[0]);
    if (!!option) dispatch(actions.updateSelectedBlueprint(option?.data));
  }, [getCategorizedBlueprintOptions, dispatch]);

  /*set blueprint from blueprint id from url params*/
  useEffect(() => {
    const id = params?.blueprint_id;
    if (!id) return;
    let option = blueprintOptions?.[0]?.options?.[0];
    if (!!id)
      option =
        find(
          flatten(map(blueprintOptions, b => b.options)),
          o => o.value === id,
        ) ?? {};
    setBlueprint(option);
    dispatch(actions.updateSelectedBlueprint(option?.data));
  }, [blueprintOptions, params?.blueprint_id, dispatch]);

  /*set environment options*/
  useEffect(() => {
    let envOptions: Record<string, any>[] = [];
    const envs = blueprint?.data?.environments;
    if (!isEmpty(envs)) {
      each(envs, o => {
        const env = environmentMapping?.[o];
        if (!!env) envOptions.push({ label: env?.name, value: o, data: env });
      });
    }
    setEnvironmentOptions(envOptions);
    setEnvironment(envOptions?.[0] ?? null);
    dispatch(actions.updateEnvironment(envOptions?.[0]?.data ?? {}));
  }, [environmentMapping, blueprint, dispatch]);

  useEffect(() => {
    const options = resourceTypeAliasOptionsWithAll;
    setResourceTypeOptions(options);
  }, [resourceTypeAliasOptionsWithAll]);

  return isEmpty(cloudAccounts.data) ? (
    <ComplianceAssessment />
  ) : (
    <Card>
      <Stack spacing="headerContentSpacing" w="full">
        <Box>
          <PageHeaderWithIcon
            label="Compliance"
            iconType={IconTypes.Security}
            iconStyles={{ p: 1.5 }}
          />
        </Box>
        <HStack justify="space-between" w="full">
          <HStack>
            <Box color="primary">By Framework</Box>
            <Box>
              <Select
                options={blueprintOptions}
                value={blueprint}
                onChange={option => {
                  setBlueprint(option);
                  dispatch(actions.updateSelectedBlueprint(option.data));
                }}
                showClearIndicator={false}
                showIconInValueContainer
                isAFilter
                menuPortalTarget={document.body}
              />
            </Box>
            <Box pl={4}>
              <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
            </Box>
            <ButtonGroup
              items={buttonGroupItems}
              value={tab}
              onClick={setTab}
            />
          </HStack>
          <HStack>
            <OutlinedIconButton
              leftIcon={<PolicyIcon p={0.5} />}
              onClick={() =>
                navigate('/policy-hub/blueprint/' + blueprint?.value)
              }
            >
              Show Blueprint
            </OutlinedIconButton>
            <OutlinedIconButton
              leftIcon={<ViewAllIcon p={1} />}
              onClick={() => setIsExpanded(true)}
            >
              Expand to Page
            </OutlinedIconButton>
            <OutlinedIconButton
              leftIcon={<ChartMixedIcon />}
              onClick={() => setShowDashboard(!showDashboard)}
              isActive={showDashboard}
            >
              Show Summary
            </OutlinedIconButton>
            <OutlinedIconButton
              leftIcon={<ThirdPartyIcon />}
              onClick={() => {
                navigate('/reports/all');
              }}
            >
              Report
            </OutlinedIconButton>
          </HStack>
        </HStack>
        {showDashboard && (
          <ComplianceDashboard environmentId={environment?.value} />
        )}
        <Box
          border="0.5px solid"
          borderColor={customTheme.colors.gray['200']}
          borderRadius={6}
          p={4}
          w="full"
          h="full"
        >
          <Stack h="full" spacing={5}>
            <HStack spacing={4}>
              <HStack>
                <Box color="primary">By Environment</Box>
                <Box>
                  <Select
                    isAFilter
                    options={environmentOptions}
                    value={environment}
                    onChange={value => {
                      setEnvironment(value);
                      dispatch(actions.updateEnvironment(value?.data));
                    }}
                    menuPortalTarget={document.body}
                    placeholder="Select environment"
                    valueContainerIcon={<EnvironmentIcon />}
                    showClearIndicator={false}
                  />
                </Box>
              </HStack>
              {!selectedBlueprint?.compliance && (
                <HStack>
                  <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>

                  <Box>
                    <Select
                      options={resourceTypeOptions}
                      value={find(
                        resourceTypeOptions,
                        o => o.value === resourceType,
                      )}
                      onChange={s => {
                        dispatch(actions.updateResourceType(s?.value));
                      }}
                      showIconInValueContainer
                      menuPortalTarget={document.body}
                      isAFilter
                      leftMessage="Resource Type"
                      showUndefinedValueLabel={false}
                    />
                  </Box>

                  <Box>
                    <Select
                      options={severityOptionsWithAll}
                      value={find(
                        severityOptionsWithAll,
                        o => o.value === severity,
                      )}
                      onChange={s =>
                        dispatch(actions.changeResourceTypeSeverity(s?.value))
                      }
                      showIconInValueContainer
                      menuPortalTarget={document.body}
                      isAFilter
                      leftMessage="Severity"
                      showUndefinedValueLabel={false}
                    />
                  </Box>
                </HStack>
              )}
            </HStack>
            <Box h="full">
              <ActiveResourceProvider>
                <ComplianceTableTabs
                  tab={tab?.value}
                  isExpanded={isExpanded}
                  setIsExpanded={setIsExpanded}
                  environmentId={environment?.value}
                />
              </ActiveResourceProvider>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
});
